import {DescriptionModel} from '../shared/description.model';
import {UserModel} from '../authentication/user.model';
import {StringExtensionModel} from '../extensions/string-extension.model';
import {Enums} from '../extensions/enums';
import { SchoolListItem } from '../school/school-list-item.interface';
import {CultureModel} from '../localization-and-pricing/culture.model';
import {NumberExtensionModel} from '../extensions/number-extension.model';
import {DateHelper} from '../extensions/date-helper';
import {COMPETITION_YEARS} from '../../landing-page/pages/competitions/competition-years';

export class CompetitionModel {

  public _id: string;
  public name: string;
  public type: string = Enums.CompetitionTypes.multiSchool;
  public translatedName = new DescriptionModel();
  public translatedDescription = new DescriptionModel();
  public startAt: Date;
  public endAt: Date;
  public redeemEndAt: Date = null;
  /** 3-letter country codes, only used by consumer competitions. */
  public countries: string[] = [];
  public isPublic = false;
  public numAnswers = 0;
  public numCorrect = 0;
  public numSchools = 0;
  public numGroups = 0;
  public numStudents = 0;
  public goals: CompetitionGoalModel[] = [];
  public createdAt: Date;
  public updatedAt: Date;

  // For starting a countdown interval
  private intervalId: ReturnType<typeof setInterval> | null;
  private countdownCallback: (() => void) | null;
  private startsIn = {
    ms: 0,
    s: 0,
    m: 0,
    h: 0,
    d: 0
  };

  // Legacy methods for backward compatibility
  static generate(json: any): CompetitionModel {
    return this.generateModel(json);
  }

  static generateList(jsonList: any): CompetitionModel[] {
    return this.generateModels(jsonList);
  }

  static generateModel(json: any): CompetitionModel {

    const competition = new CompetitionModel();
    competition._id = json._id;
    competition.name = json.name;
    competition.type = json.type;
    if (json.countries) {
      competition.countries = json.countries;
    }
    competition.isPublic = json.isPublic || false;
    competition.numAnswers = json.numAnswers || 0;
    competition.numCorrect = json.numCorrect || 0;
    competition.numSchools = json.numSchools || 0;
    competition.numGroups = json.numGroups || 0;
    competition.numStudents = json.numStudents || 0;

    if (json.startAt) {
      competition.startAt = new Date(json.startAt);
    }

    if (json.endAt) {
      competition.endAt = new Date(json.endAt);
    }

    if (json.redeemEndAt) {
      competition.redeemEndAt = new Date(json.redeemEndAt);
    }

    if (json.translatedName) {
      competition.translatedName = DescriptionModel.generateModel(json.translatedName);
    }

    if (json.translatedDescription) {
      competition.translatedDescription = DescriptionModel.generateModel(json.translatedDescription);
    }

    if (json.goals && json.goals.length > 0) {
      competition.goals = CompetitionGoalModel.generateModels(json.goals);
    }

    if (json.createdAt) {
      competition.createdAt = new Date(json.createdAt);
    }

    if (json.updatedAt) {
      competition.updatedAt = new Date(json.updatedAt);
    }

    return competition;
  }

  static generateModels(jsonList: any[]): CompetitionModel[] {
    const list = [];
    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }
    return list;
  }

  startCountdown(onEnd?: () => void) {

    if (!this.intervalId && this.isUpcoming() && this.startAt && this.endAt) {

      this.countdownCallback = onEnd;
      this.intervalId = setInterval(() => {

        if (this.isUpcoming()) {
          this.startsIn = DateHelper.diffBetweenDates(this.startAt);
        } else {
          this.endCountdown();
        }

      }, 1000);

    }

  }

  endCountdown() {

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.countdownCallback) {
      this.countdownCallback();
      this.countdownCallback = null;
    }

  }

  getCountdownDays(): string {

    return this.startsIn.d.toString(10);

  }

  getCountdownHours(): string {

    return StringExtensionModel.addZero(this.startsIn.h);
  }

  getCountdownMinutes(): string {

    return StringExtensionModel.addZero(this.startsIn.m);
  }

  getCountdownSeconds(): string {

    return StringExtensionModel.addZero(this.startsIn.s);
  }

  getTwoInitials(): string {
    return StringExtensionModel.getInitials(this.name, 2);
  }

  getHomepageName(): string {

    const homepageCulture = CultureModel.getHomepageCulture();
    if (this.translatedName) {
      return this.translatedName[homepageCulture];
    }

    return null;
  }

  getHomepageDescription(): string {

    const homepageCulture = CultureModel.getHomepageCulture();
    if (this.translatedDescription) {
      return this.translatedDescription[homepageCulture];
    }

    return null;
  }

  getTranslatedNameForUser(): string {

    const user = UserModel.getCurrent();
    if (user && user.culture && this.translatedName) {
      return this.translatedName[user.culture];
    }

    return null;
  }

  getTranslatedDescriptionForUser(): string {

    const user = UserModel.getCurrent();
    if (user && user.culture && this.translatedDescription) {
      return this.translatedDescription[user.culture];
    }

    return null;
  }

  isActive(): boolean {
    const now = new Date();
    return this.startAt <= now && this.endAt >= now;
  }

  isUpcoming(): boolean {
    const now = new Date();
    return this.startAt > now;
  }

  isFinished(): boolean {
    const now = new Date();
    return this.endAt < now;
  }

  getDurationInDays(): number {
    if (this.startAt && this.endAt) {
      const diffTime = Math.abs(this.endAt.getTime() - this.startAt.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  }

  getDaysLeft(): number {

    // Get current date and end date
    const now = new Date();
    const endDate = new Date(this.endAt);

    // Calculate time difference in milliseconds
    const diffTime = endDate.getTime() - now.getTime();

    // Convert milliseconds to days and round up using Math.ceil
    // Formula: milliseconds / (1000 ms * 60 sec * 60 min * 24 hours)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Return days left if positive, otherwise return 0 (competition has ended)
    return diffDays > 0 ? diffDays : 0;

  }

  isConsumer(): boolean {
    return this.type === Enums.CompetitionTypes.consumer;
  }

  getCompletedGoalsCount(): number {
    return this.goals.filter(goal => goal.completed).length;
  }

  getTotalGoalsCount(): number {
    return this.goals.length;
  }

  getProgressPercentage(): number {

    if (this.goals.length === 0) {
      return 0;
    }
    return Math.round((this.getCompletedGoalsCount() / this.getTotalGoalsCount()) * 100);
  }

  /**
   * Whether the competition counts only correct answers. 2025 counted every answer, so it is listed
   * as false in COMPETITION_YEARS; a competition that is not listed counts correct answers.
   */
  countsCorrectAnswers(): boolean {

    for (const year of COMPETITION_YEARS) {
      if (year.id === this._id) {
        return year.countsCorrectAnswers;
      }
    }

    return true;
  }

  /**
   * The answer total the competition counts: numCorrect when it counts correct answers, otherwise numAnswers.
   */
  getCountedAnswers(): number {

    if (this.countsCorrectAnswers()) {
      return this.numCorrect;
    }

    return this.numAnswers;
  }

  getAvgAnswers(): number {

    if (this.numStudents > 0) {
      return this.getCountedAnswers() / this.numStudents;
    }

    return 0;
  }

  getAvgAnswersWithDelimiter(): string {

    return this.delimiterForNumber(this.getAvgAnswers());
  }

  getNumAnswersWithDelimiter(): string {

    return this.delimiterForNumber(this.getCountedAnswers());
  }

  getNumSchoolsWithDelimiter(): string {

    return this.delimiterForNumber(this.numSchools);
  }

  getNumStudentsWithDelimiter(): string {

    return this.delimiterForNumber(this.numStudents);
  }

  getGroupsWithDelimiter(): string {

    return this.delimiterForNumber(this.numGroups);
  }

  private delimiterForNumber(value: number) {

    const delimiter = '.';
    return NumberExtensionModel.delimiterSeparatedNumber(value, delimiter);

  }

  // Goal methods

  /**
   * Returns the goal that is currently being worked on. If none is found
   * and we have more than 0 answers we send the last one which is most likely
   * over.
   */
  getCurrentGoal() {

    const numAnswers = this.getCountedAnswers();
    for (const goal of this.goals) {

      if (numAnswers >= goal.startValue && numAnswers <= goal.endValue) {
        return goal;
      }

    }

    if (numAnswers > 0 && this.goals.length > 0) {
      return this.goals[this.goals.length - 1];
    }

    return null;
  }

  /**
   * The mastery of the current goal
   */
  getCurrentGoalMastery() {

    const currentGoal = this.getCurrentGoal();
    if (currentGoal) {
      return currentGoal.getMastery(this.getCountedAnswers());
    }

    return 0;
  }

  getGoalMastery(index: number): number {

    if (index < this.goals.length) {
      return this.goals[index].getMastery(this.getCountedAnswers());
    }

    return 0;
  }

  getGoalUseShadowImage(index: number): boolean {

    const goalMastery = this.getGoalMastery(index);
    if (index < this.goals.length) {

      const goal = this.goals[index];
      return goal.useShadowImage && goalMastery < 100;

    }

    return false;

  }

  getGoalName(index: number, culture: string = CultureModel.getHomepageCulture()): string {

    if (index < this.goals.length) {
      return this.goals[index].translatedName.stringForCulture(culture);
    }

    return '';

  }

}

export class CompetitionGoalModel {

  public _id: string;
  public goalType: string;
  public imgURL: string;
  /**
   * All reward images for the goal. Use getImages() to read them since older
   * goals only have imgURL.
   */
  public imgURLs: string[] = [];
  public useLocalImage = true;
  public useShadowImage = true;
  /** The reward is a new feature in the game rather than an item. */
  public isNewFeature = false;
  public startValue = 0;
  public endValue: number;
  public translatedName = new DescriptionModel();
  public translatedDescription = new DescriptionModel();
  public completed = false;
  public completedAt: Date = null;
  public integrationId: string;

  static generateModel(json: any): CompetitionGoalModel {

    const goal = new CompetitionGoalModel();
    goal._id = json._id;
    goal.goalType = json.goalType;
    goal.imgURL = json.imgURL;
    if (json.imgURLs) {
      goal.imgURLs = json.imgURLs;
    }
    goal.useLocalImage = json.useLocalImage;
    goal.useShadowImage = json.useShadowImage;
    goal.isNewFeature = json.isNewFeature || false;
    goal.startValue = json.startValue || 0;
    goal.endValue = json.endValue;
    goal.completed = json.completed || false;
    goal.completedAt = json.completedAt ? new Date(json.completedAt) : null;
    goal.integrationId = json.integrationId;

    if (json.translatedName) {
      goal.translatedName = DescriptionModel.generateModel(json.translatedName);
    }

    if (json.translatedDescription) {
      goal.translatedDescription = DescriptionModel.generateModel(json.translatedDescription);
    }

    return goal;
  }

  static generateModels(jsonList: any[]): CompetitionGoalModel[] {
    const list = [];
    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }
    return list;
  }

  /**
   * The reward images for the goal, falling back to the single imgURL for goals
   * created before there could be several.
   */
  getImages(): string[] {

    if (this.imgURLs && this.imgURLs.length > 0) {
      return this.imgURLs;
    }

    if (this.imgURL) {
      return [this.imgURL];
    }

    return [];
  }

  completedAtString(): string {

    if (this.completedAt) {
      return this.completedAt.toISOString();
    }

    return 'Not completed';

  }

  /**
   *
   * @param numAnswers The toal number of answers currently answered in the compatition
   * tu use as a reference number for completion.
   */
  getMastery(numAnswers: number): number {

    const partialTotal = (this.endValue - this.startValue);
    const value = (numAnswers - this.startValue) / partialTotal;

    if (value < 0) {
      return 0;
    } else if (value > 1) {
      return 100;
    }

    return value * 100;
  }


}

/**
 * Shared interface for competition school data used in leaderboards
 * This allows both CompetitionParticipantSchoolModel and CompetitionSchoolActivityModel
 * to be used interchangeably in components
 */
export interface ICompetitionSchool {
  country: string;
  numStudents: number;
  numAnswers: number;
  numCorrect: number;
  getName(): string;
}

export class CompetitionParticipantSchoolModel implements SchoolListItem, ICompetitionSchool {

  public _id: string;
  public name: string;
  public country: string;
  public competitionId: string;
  public startAt: Date;
  public endAt: Date;
  public schoolId: string;
  public numStudents = 0;
  public numGroups = 0;
  public numAnswers = 0;
  public numCorrect = 0;
  public avgAnswers = 0;
  public avgCorrect = 0;
  public timeSpent = 0;
  public createdAt: Date;
  public updatedAt: Date;
  public school?: any; // Nested school data when includeSchool=1

  static generateModel(json: any): CompetitionParticipantSchoolModel {
    const participant = new CompetitionParticipantSchoolModel();
    participant._id = json._id;
    participant.name = json.name;
    participant.country = json.country;
    participant.competitionId = json.competitionId;
    participant.schoolId = json.schoolId;
    participant.numStudents = json.numStudents || 0;
    participant.numGroups = json.numGroups || 0;
    participant.numAnswers = json.numAnswers || 0;
    participant.numCorrect = json.numCorrect || 0;
    participant.avgAnswers = json.avgAnswers || 0;
    participant.avgCorrect = json.avgCorrect || 0;
    participant.timeSpent = json.timeSpent || 0;

    if (json.startAt) {
      participant.startAt = new Date(json.startAt);
    }

    if (json.endAt) {
      participant.endAt = new Date(json.endAt);
    }

    if (json.createdAt) {
      participant.createdAt = new Date(json.createdAt);
    }

    if (json.updatedAt) {
      participant.updatedAt = new Date(json.updatedAt);
    }

    // Include nested school data if present (when includeSchool=1)
    if (json.school) {
      participant.school = json.school;
    }

    return participant;
  }

  static generateModels(jsonList: any[]): CompetitionParticipantSchoolModel[] {
    const list = [];
    for (const json of jsonList) {
      list.push(this.generateModel(json));
    }
    return list;
  }

  isActive(): boolean {
    const now = new Date();
    return this.startAt <= now && this.endAt >= now;
  }

  isUpcoming(): boolean {
    const now = new Date();
    return this.startAt > now;
  }

  isFinished(): boolean {
    const now = new Date();
    return this.endAt < now;
  }

  // Get school name (either from participant name or nested school)
  getSchoolName(): string {
    if (this.school && this.school.name) {
      return this.school.name;
    }
    return this.name;
  }

  // Get school address (from nested school data)
  getSchoolAddress(): string {
    if (this.school && this.school.address) {
      return this.school.address;
    }
    return null;
  }

  // Get school city (from nested school data)
  getSchoolCity(): string {
    if (this.school && this.school.city) {
      return this.school.city;
    }
    return null;
  }

  // Convert to SchoolModel if nested school data is available
  toSchoolModel(): any {
    if (this.school) {
      return this.school;
    }
    return null;
  }

  // Check if this participant school has nested school data
  hasSchoolData(): boolean {
    return this.school !== null && this.school !== undefined;
  }

  // Get two initials for the avatar display
  getTwoInitials(): string {
    return StringExtensionModel.getInitials(this.getSchoolName(), 2);
  }

  // Implementation of ICompetitionSchool interface
  getName(): string {
    return this.getSchoolName();
  }
}

export class CompetitionFilter {

  skip = 0;
  limit = 20;

  name: string = undefined;
  type: string = undefined;
  isPublic: boolean = undefined;
  startAt: Date = undefined;
  endAt: Date = undefined;
  schoolId: string = undefined;
  isActive: boolean = undefined;

  sortByName: number = undefined;
  sortByStartAt: number = undefined;
  sortByEndAt: number = undefined;

  populateGoals: boolean = undefined;
  populateParticipants: boolean = undefined;
}

export class CompetitionParticipantSchoolFilter {
  skip = 0;
  limit = 20;
  sortByName: number = undefined;
  sortByAvgAnswers: number = undefined;
  sortByAvgCorrect: number = undefined;
  country: string = undefined;
  name: string = undefined;
  includeSchool: boolean = undefined;
  includeSchoolAsRoot: boolean = undefined;
}

export class PublicCompetitionParticipantSchoolFilter {
  limit = 20;
  sortByAvgAnswers: number = undefined;
  sortByNumAnswers: number = undefined;
  sortByNumCorrect: number = undefined;
  sortByAvgCorrect: number = undefined;
  sortByTimeSpent: number = undefined;
}
